import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';
import { planStatusLabel } from './planConstants';

export default function PlatformPlanDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = usePlatformAuth();
  const isSuper = user?.role === 'super_admin';

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const q = isSuper ? '?includeDeleted=1' : '';
    try {
      const res = await platformApiFetch(`/api/platform/plans/${id}${q}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setPlan(json.plan);
    } catch (e) {
      setError(e.message);
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [id, isSuper]);

  useEffect(() => {
    load();
  }, [load]);

  const postStatus = async (status) => {
    setBusy(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/plans/${id}/status`, {
        method: 'POST',
        body: { status }
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Update failed');
      setPlan(json.plan);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const softDelete = async () => {
    if (!window.confirm('Soft-delete this plan? It will be hidden from tenants and default admin lists.')) return;
    setBusy(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/plans/${id}`, { method: 'DELETE' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Delete failed');
      setPlan(json.plan);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/plans/${id}/restore`, { method: 'POST', body: {} });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Restore failed');
      setPlan(json.plan);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !plan) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!plan) return null;

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4">{plan.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            <code>{plan.code}</code>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate('/platform/plans')}>
            Back to list
          </Button>
          {isSuper && !plan.deleted_at && (
            <Button variant="contained" onClick={() => navigate(`/platform/plans/${id}/edit`)}>
              Edit
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip label={planStatusLabel(plan.status)} />
          {plan.deleted_at && <Chip color="warning" label="Soft deleted" />}
        </Box>
        {isSuper && !plan.deleted_at && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Button size="small" disabled={busy} onClick={() => postStatus('active')}>
              Activate
            </Button>
            <Button size="small" disabled={busy} onClick={() => postStatus('inactive')}>
              Deactivate
            </Button>
            <Button size="small" disabled={busy} onClick={() => postStatus('draft')}>
              Draft
            </Button>
            <Button size="small" disabled={busy} onClick={() => postStatus('archived')}>
              Archive
            </Button>
            <Button size="small" color="error" disabled={busy} onClick={softDelete}>
              Soft delete
            </Button>
          </Box>
        )}
        {isSuper && plan.deleted_at && (
          <Box sx={{ mb: 2 }}>
            <Button variant="contained" color="secondary" disabled={busy} onClick={restore}>
              Restore
            </Button>
          </Box>
        )}
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          {row('Description', plan.description || '—')}
          {row('Monthly price', Number(plan.monthly_price).toFixed(2))}
          {row('Annual price', Number(plan.annual_price).toFixed(2))}
          {row('API call limit', plan.api_call_limit)}
          {row('Storage limit (MB)', plan.storage_limit_mb)}
          {row('Max users', plan.max_users)}
          {row('Max API keys', plan.max_api_keys)}
          {row('Trial days', plan.trial_days)}
          {row('Sort order', plan.sort_order)}
          {row('Created', plan.created_at ? String(plan.created_at) : '—')}
          {row('Updated', plan.updated_at ? String(plan.updated_at) : '—')}
          {plan.deleted_at && row('Deleted at', String(plan.deleted_at))}
        </Grid>
      </Paper>
    </Box>
  );
}
